import { Popover } from "@mui/material";
import { useRef, useState } from "react";

import { RobotoTooltip } from "@/shared/components";
import {
  AccessTimeIcon,
  SecurityIcon,
  SettingsIcon,
  TuneIcon,
} from "@/shared/foundation/icons/mui";

import styles from "./ThreadSettingsButton.module.css";

interface ThreadSettingsButtonProps {
  onOpenToolSettings: () => void;
  onOpenContextSettings: () => void;
  onOpenTimeFormatSettings: () => void;
}

/**
 * Gear button that opens a popover with the thread's settings: tool
 * permissions, context settings, and time format.
 *
 * Gear, not "+": every entry changes thread behaviour, whereas "+" in a chat
 * composer reads as attach/upload.
 */
export function ThreadSettingsButton({
  onOpenToolSettings,
  onOpenContextSettings,
  onOpenTimeFormatSettings,
}: ThreadSettingsButtonProps) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const handleItemClick = (action: () => void) => {
    setOpen(false);
    action();
  };

  return (
    <>
      <RobotoTooltip title="Thread settings" placement="top">
        <button
          ref={anchorRef}
          type="button"
          className={styles.button}
          onClick={() => setOpen((prev) => !prev)}
          aria-label="Thread settings"
          aria-haspopup="true"
          aria-expanded={open}
        >
          <SettingsIcon className={styles.icon} />
        </button>
      </RobotoTooltip>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: { className: styles.popover },
        }}
      >
        <button
          type="button"
          className={styles.menuItem}
          onClick={() => handleItemClick(onOpenToolSettings)}
        >
          <SecurityIcon className={styles.menuItemIcon} />
          Tool permissions
        </button>
        <button
          type="button"
          className={styles.menuItem}
          onClick={() => handleItemClick(onOpenContextSettings)}
        >
          <TuneIcon className={styles.menuItemIcon} />
          Context settings
        </button>
        <button
          type="button"
          className={styles.menuItem}
          onClick={() => handleItemClick(onOpenTimeFormatSettings)}
        >
          <AccessTimeIcon className={styles.menuItemIcon} />
          Time format
        </button>
      </Popover>
    </>
  );
}
